import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { RefreshCw, ShieldAlert, Loader2 } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const getAuthConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const fmt = (iso) => {
  if (!iso) return "—";
  try { return format(parseISO(iso), "MMM d, yyyy h:mm a"); } catch { return iso; }
};

const duration = (log) => {
  const start = log.started_at ? Date.parse(log.started_at) : null;
  const end = log.ended_at ? Date.parse(log.ended_at) : (log.active ? Date.now() : (log.expires_at ? Date.parse(log.expires_at) : null));
  if (!start || !end) return "—";
  const mins = Math.max(0, Math.round((end - start) / 60000));
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

export default function AdminSupportAccessLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/impersonation/logs`, getAuthConfig());
      setLogs(res.data.logs || []);
    } catch {
      toast.error("Failed to load support access log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="support-access-log-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope'] flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-amber-500" />Support Access Log
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Every "Log in as customer" session — who accessed which account, when, and from where.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-access-log-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1.5 hidden sm:inline">Refresh</span>
        </Button>
      </div>

      <Card className="bg-card border-theme">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">No support access sessions recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-theme text-muted-foreground text-xs">
                    <th className="text-left font-medium px-4 py-2.5">Admin</th>
                    <th className="text-left font-medium px-4 py-2.5">Customer</th>
                    <th className="text-left font-medium px-4 py-2.5">Started</th>
                    <th className="text-left font-medium px-4 py-2.5">Ended</th>
                    <th className="text-left font-medium px-4 py-2.5">Duration</th>
                    <th className="text-left font-medium px-4 py-2.5">IP Address</th>
                    <th className="text-left font-medium px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.log_id} className="border-b border-theme/50 hover:bg-muted/30" data-testid={`access-log-row-${l.log_id}`}>
                      <td className="px-4 py-2.5 whitespace-nowrap">{l.admin_email || l.admin_user_id}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="font-medium text-foreground">{l.target_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{l.target_email}</div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{fmt(l.started_at)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{l.ended_at ? fmt(l.ended_at) : (l.active ? "—" : `expired ${fmt(l.expires_at)}`)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">{duration(l)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap font-mono text-xs">{l.ip_address || "—"}</td>
                      <td className="px-4 py-2.5">
                        {l.active ? (
                          <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25 text-[11px] font-semibold">Active</Badge>
                        ) : (
                          <Badge className="bg-muted text-muted-foreground text-[11px]">Ended</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
